import type {ConnectionADto} from '@cohort/admin-schemas/connection';
import type {ConnectionEditComponentProps} from '@cohort/merchants/apps';
import Button from '@cohort/merchants/components/buttons/Button';
import ConnectionHeader from '@cohort/merchants/components/connections/ConnectionHeader';
import {notify} from '@cohort/merchants/hooks/toast';
import useOAuthFlow, {OAuthFlowStatus} from '@cohort/merchants/hooks/useOAuthFlow';
import type {AppId} from '@cohort/shared/apps';
import {getAppSpec} from '@cohort/shared/apps';
import type {CohortErrorCode} from '@cohort/shared/schema/common/errors';
import {CheckCircle, CirclesThreePlus, WarningCircle} from '@phosphor-icons/react';
import React, {Fragment, useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {match, P} from 'ts-pattern';

export type CustomOAuthErrorMessage = (cause: CohortErrorCode) => string | undefined;

export type PreConnectionConfigComponentProps = {
  connection?: ConnectionADto;
  onConfigFinished: (config: Record<string, unknown>) => void;
  onClose?: (connection: ConnectionADto | null) => void;
  showCancelBtn?: boolean;
};

export type PostConnectionConfigComponentProps = {
  connection: ConnectionADto;
  onConfigFinished: (connection: ConnectionADto) => void;
};

type OAuthEditConnectionComponentProps = ConnectionEditComponentProps & {
  appId: AppId;
  customOauthErrorMessage?: CustomOAuthErrorMessage;
  OAuthNoticeComponent?: React.FC;
  PreConnectionConfigComponent?: React.FC<PreConnectionConfigComponentProps>;
  PostConnectionConfigComponent?: React.FC<PostConnectionConfigComponentProps>;
};

const OAuthEditConnectionComponent: React.FC<OAuthEditConnectionComponentProps> = ({
  existingConnection,
  autoStartOauthFlow,
  onClose,
  appId,
  OAuthNoticeComponent,
  customOauthErrorMessage,
  PreConnectionConfigComponent,
  PostConnectionConfigComponent,
  showCancelBtn,
}) => {
  const [reconnect, setReconnect] = useState(false);

  const [connection, setConnection] = useState<ConnectionADto | undefined>(existingConnection);
  const [preConfig, setPreConfig] = useState<Record<string, unknown> | undefined>(undefined);
  const appSpec = getAppSpec(appId);

  const {t} = useTranslation('components', {
    keyPrefix: 'connections.oauthEditConnectionComponent',
  });

  const handleOauthFlowCompleted = (connection: ConnectionADto | null): void => {
    if (connection) {
      setConnection(connection);
      onClose?.(connection);
      notify('success', t('connectionSuccessful', {appName: appSpec.name}));
    }
  };

  const {
    startOAuthFlow,
    status: oauthFlowStatus,
    cancelOAuthFlow,
  } = useOAuthFlow({
    appSpec,
    existingConnectionId: existingConnection?.id,
    preConfig,
    onCompleted: handleOauthFlowCompleted,
    customOauthErrorMessage,
    autoStartOauthFlow: PreConnectionConfigComponent === undefined && autoStartOauthFlow === true,
  });

  const onContainerRendered = useCallback((node: HTMLElement | null) => {
    if (node && PreConnectionConfigComponent === undefined && autoStartOauthFlow === true) {
      startOAuthFlow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ReconnectButton: React.FC = () => (
    <Button
      type="submit"
      variant="secondary"
      onClick={() => {
        if (PreConnectionConfigComponent) {
          setReconnect(true);
        } else {
          startOAuthFlow();
        }
      }}
    >
      {t('buttonReconnect')}
    </Button>
  );

  const content = match({
    connection,
    reconnect,
    oauthFlowStatus,
    preConfig,
    PreConnectionConfigComponent,
    PostConnectionConfigComponent,
  })
    .with(
      {
        connection: undefined,
        PreConnectionConfigComponent: undefined,
        oauthFlowStatus: P.not(OAuthFlowStatus.InProgress),
      },
      () => (
        <Fragment>
          <ConnectionHeader
            appId={appId}
            title={
              <span className="text-base font-medium">
                {t('titleConnect', {appName: appSpec.name})}
              </span>
            }
            actions={[
              <Button key="connect" type="submit" className="flex gap-2" onClick={startOAuthFlow}>
                <CirclesThreePlus className="h-5 w-5" />
                {t('buttonConnect')}
              </Button>,
              ...(showCancelBtn
                ? [
                    <Button
                      key="cancel"
                      type="button"
                      variant="secondary"
                      className="flex gap-2"
                      onClick={() => onClose?.(null)}
                    >
                      {t('buttonCancel')}
                    </Button>,
                  ]
                : []),
            ]}
          />
          {OAuthNoticeComponent !== undefined && <OAuthNoticeComponent />}
        </Fragment>
      )
    )
    .with(
      {
        connection: undefined,
        PreConnectionConfigComponent: P.not(undefined),
        oauthFlowStatus: P.not(OAuthFlowStatus.InProgress),
      },
      {
        connection: P.not(undefined),
        PreConnectionConfigComponent: P.not(undefined),
        reconnect: true,
      },
      ({PreConnectionConfigComponent}) => (
        <Fragment>
          <ConnectionHeader
            appId={appId}
            title={
              <span className="text-base font-medium">
                {t('titleConnect', {appName: appSpec.name})}
              </span>
            }
          />
          <PreConnectionConfigComponent
            onConfigFinished={config => {
              setPreConfig(config);
              setReconnect(false);
              startOAuthFlow();
            }}
            showCancelBtn={showCancelBtn}
            onClose={onClose}
          />
        </Fragment>
      )
    )
    .with({oauthFlowStatus: OAuthFlowStatus.InProgress}, () => (
      <Fragment>
        <ConnectionHeader
          appId={appId}
          title={
            <span className="block w-[340px] text-sm font-normal text-slate-500">
              {t('titleConnecting')}
            </span>
          }
          actions={
            <Fragment>
              <Button className="w-24" variant="secondary" type="button" onClick={cancelOAuthFlow}>
                {t('buttonCancel')}
              </Button>
              <Button className="w-24" type="button" loading={true} />
            </Fragment>
          }
        />
        {OAuthNoticeComponent !== undefined && <OAuthNoticeComponent />}
      </Fragment>
    ))
    .with({connection: {status: 'broken'}}, () => (
      <Fragment>
        <ConnectionHeader
          appId={appId}
          title={
            <p className="flex gap-1">
              <WarningCircle className="h-5 w-5 text-red-500" />
              <span className="text-sm font-medium text-red-700">{t('titleBroken')}</span>
            </p>
          }
          actions={<ReconnectButton />}
        />
        {OAuthNoticeComponent !== undefined && <OAuthNoticeComponent />}
      </Fragment>
    ))
    .with({connection: {status: 'ready'}}, () => (
      <ConnectionHeader
        appId={appId}
        title={
          <p className="flex gap-1">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-sm font-medium text-green-700">{t('titleReady')}</span>
          </p>
        }
        actions={<ReconnectButton />}
      />
    ))
    .with(
      {connection: {status: 'config_needed'}, PostConnectionConfigComponent: P.not(undefined)},
      ({connection, PostConnectionConfigComponent}) => (
        <PostConnectionConfigComponent
          connection={connection}
          onConfigFinished={connection => {
            setConnection(connection);
            onClose?.(connection);
          }}
        />
      )
    )
    .otherwise(() => null);

  return (
    <div
      ref={onContainerRendered}
      className="flex flex-col items-center justify-center space-y-4 p-6"
    >
      {content}
    </div>
  );
};

export default OAuthEditConnectionComponent;
